import { BetrokkenTrein, useGetDossierBetrokkenTreinenById } from "@/entities/betrokken-treinen";
import { usePutNazorg } from "@/entities/dossier-details";
import {
    Button,
    Checkbox,
    Checkboxfield,
    ConfirmDialog,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    ErrorDisplay,
    Label,
    Skeleton,
} from "@/shared/components";
import { useState } from "react";

type NazorgDialogProps = {
    dossierId: string;
    open: boolean;
    setOpen: (open: boolean) => void;
};

type Step = "voorwaarden" | "treinen";

export function NazorgDialog({ dossierId, open, setOpen }: NazorgDialogProps) {
    const [step, setStep] = useState<Step>("voorwaarden");
    const [geselecteerdeTreinen, setGeselecteerdeTreinen] = useState<string[]>([]);
    const [confirmZonderTreinenOpen, setConfirmZonderTreinenOpen] = useState(false);
    const putNazorg = usePutNazorg();

    const activeerNazorg = () => {
        putNazorg.mutate({ dossierId, betrokkenTreinen: geselecteerdeTreinen }, { onSuccess: () => setOpen(false) });
    };

    const handleActiveren = () => {
        if (geselecteerdeTreinen.length === 0) {
            putNazorg.reset();
            setConfirmZonderTreinenOpen(true);
            return;
        }
        activeerNazorg();
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContainer data-testid="nazorg-dialog">
                <DialogHeader>
                    <DialogTitle>Nazorg bij Incidenten</DialogTitle>
                </DialogHeader>
                {step === "voorwaarden" && <VoorwaardenStep onCancel={() => setOpen(false)} onNext={() => setStep("treinen")} />}
                {step === "treinen" && (
                    <TreinenStep
                        dossierId={dossierId}
                        geselecteerdeTreinen={geselecteerdeTreinen}
                        setGeselecteerdeTreinen={setGeselecteerdeTreinen}
                        onCancel={() => setOpen(false)}
                        onSubmit={handleActiveren}
                        isPending={putNazorg.isPending}
                        error={confirmZonderTreinenOpen ? null : putNazorg.error}
                    />
                )}
            </DialogContainer>
            <ConfirmDialog
                isOpen={confirmZonderTreinenOpen}
                onCancel={() => setConfirmZonderTreinenOpen(false)}
                onConfirm={activeerNazorg}
                isPending={putNazorg.isPending}
                error={putNazorg.error}
                title="Nazorg bij Incidenten"
                confirmLabel="Activeren"
                cancelLabel="Annuleren"
            >
                Er zijn geen treinen in het dossier toegevoegd. Mocht de nazorg betrekking hebben op een specifieke trein, voeg deze dan
                eerst toe aan het dossier.
            </ConfirmDialog>
        </Dialog>
    );
}

type VoorwaardenStepProps = {
    onCancel: () => void;
    onNext: () => void;
};

function VoorwaardenStep({ onCancel, onNext }: VoorwaardenStepProps) {
    const [voldoetAanVereisten, setVoldoetAanVereisten] = useState(false);
    const [showVereistenError, setShowVereistenError] = useState(false);

    const handleVolgende = () => {
        if (!voldoetAanVereisten) {
            setShowVereistenError(true);
            return;
        }
        onNext();
    };

    return (
        <>
            <DialogBody className="flex flex-col gap-4">
                <Vereisten />
                <Checkboxfield
                    id="nazorg-voldoet-aan-vereisten"
                    label="Dit dossier voldoet aan bovenstaande vereisten"
                    checked={voldoetAanVereisten}
                    onCheckedChange={(checked) => {
                        setVoldoetAanVereisten(checked);
                        if (checked) setShowVereistenError(false);
                    }}
                    className="gap-x-2"
                    errorMessage={showVereistenError ? "Controleer of aan alle vereisten is voldaan" : undefined}
                />
            </DialogBody>
            <DialogFooter>
                <Button type="button" variant="tertiary" onClick={onCancel}>
                    Annuleren
                </Button>
                <Button type="button" variant="primary" onClick={handleVolgende}>
                    Volgende
                </Button>
            </DialogFooter>
        </>
    );
}

type TreinenStepProps = {
    dossierId: string;
    geselecteerdeTreinen: string[];
    setGeselecteerdeTreinen: (treinen: string[]) => void;
    onCancel: () => void;
    onSubmit: () => void;
    isPending: boolean;
    error: Error | null;
};

function TreinenStep({ dossierId, geselecteerdeTreinen, setGeselecteerdeTreinen, onCancel, onSubmit, isPending, error }: TreinenStepProps) {
    const treinen = useGetDossierBetrokkenTreinenById(dossierId, (data) => data.filter((trein) => trein.isRelevant));
    const heeftTreinen = !!treinen.data && treinen.data.length > 0;

    const toggleTrein = (label: string, checked: boolean) => {
        setGeselecteerdeTreinen(checked ? [...geselecteerdeTreinen, label] : geselecteerdeTreinen.filter((t) => t !== label));
    };

    return (
        <>
            <DialogBody className="flex flex-col gap-4">
                {treinen.isPending && <Skeleton className="h-24 w-full" />}
                {treinen.error && <ErrorDisplay error={treinen.error} title="Fout bij het laden van betrokken treinen" />}
                {treinen.data && !heeftTreinen && (
                    <p className="text-content-default text-m">
                        Er zijn geen treinen in het dossier toegevoegd. Mocht de nazorg betrekking hebben op een specifieke trein, voeg deze
                        dan eerst toe aan het dossier.
                    </p>
                )}
                {heeftTreinen && (
                    <>
                        <p className="text-content-default text-m">Voor welk van de volgende treinen moet nazorg geactiveerd worden?</p>
                        <fieldset className="flex flex-col gap-2">
                            <Label asChild>
                                <legend>
                                    Nazorg voor trein <span className="font-normal">Optioneel</span>
                                </legend>
                            </Label>
                            {treinen.data.map((trein) => {
                                const label = BetrokkenTrein.formatTrainLabel(trein);
                                const id = `nazorg-trein-${trein.id}`;
                                return (
                                    <div key={trein.id} className="flex items-center gap-2">
                                        <Checkbox
                                            id={id}
                                            checked={geselecteerdeTreinen.includes(label)}
                                            onCheckedChange={(checked) => toggleTrein(label, checked === true)}
                                            data-testid={`check-${id}`}
                                        />
                                        <Label htmlFor={id} className="text-content-default text-m cursor-pointer font-normal">
                                            {label}
                                        </Label>
                                    </div>
                                );
                            })}
                        </fieldset>
                    </>
                )}
            </DialogBody>
            <DialogFooter>
                <Button type="button" variant="tertiary" onClick={onCancel}>
                    Annuleren
                </Button>
                <Button type="button" variant="primary" onClick={onSubmit} disabled={isPending || treinen.isPending || !!treinen.error}>
                    Activeren
                </Button>
                {error && <ErrorDisplay error={error} />}
            </DialogFooter>
        </>
    );
}

function Vereisten() {
    return (
        <div className="flex flex-col gap-2">
            <p>Wil je Nazorg bij incidenten activeren voor dit dossier?</p>
            <div>
                <p>Activeer Nazorg bij een groep reizigers met een impactvolle gebeurtenis:</p>
                <ul className="list-disc pl-6">
                    <li>Grote calamiteit op het station (bv. reizigers moeten overnachten)</li>
                    <li>Inzet van de opvangtrein</li>
                    <li>Ingrijpend ongeval met de trein</li>
                    <li>Langdurige stranding op de vrije baan met veel emotionele of fysieke impact</li>
                </ul>
            </div>
            <div>
                <p>Niet activeren bij:</p>
                <ul className="list-disc pl-6">
                    <li>Gestrande reizigers op een station zonder grote crisis</li>
                    <li>Een individuele reiziger — hiervoor loopt het bestaande proces via Meldkamer NS</li>
                </ul>
            </div>
            <p>
                <strong>Let op:</strong> de RVKB overlegt eerst met personeel ter plaatse.
            </p>
        </div>
    );
}
